import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLifeAreas } from "./config";
import { Composite } from "./lifeAreasTheme";
import { fs, ink, T, withAlpha } from "./tokens";
import { DimensionKey, RingConfig, RingDimension, RingSnapshot } from "./types";
import { useReducedMotion } from "./useReducedMotion";

// Spec layout.ring_canvas: Core Self r60 and rings at r120, r180, r240 (60 apart)
// with a 36 stroke. Each ring is split into arcs for the areas the config assigns
// to it; an arc fills clockwise by score. The ring list comes from the config.
const C = 300;
const CORE_R = 60;
const RING_BASE = 120;
const RING_STEP = 60;
const STROKE = 36;
const EXTRA_GAP_DEG = 2.5;
const SELECT_SCALE = 1.03;
const LOAD_MS = 900;
const STAGGER_MS = 80;
const EFFECT_MS = 700;

const rad = (deg: number) => (deg * Math.PI) / 180;
const pt = (r: number, deg: number): [number, number] => [
  C + r * Math.cos(rad(deg)),
  C + r * Math.sin(rad(deg)),
];
const arc = (r: number, a0: number, a1: number) => {
  const [x0, y0] = pt(r, a0);
  const [x1, y1] = pt(r, a1);
  return `M${x0.toFixed(2)} ${y0.toFixed(2)} A${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
};

type Seg = { key: DimensionKey; ringLabel: string; radius: number; a0: number; a1: number; mid: number; index: number };
const buildSegments = (rings: RingConfig[]): Seg[] => {
  const out: Seg[] = [];
  let index = 0;
  rings.forEach((ring, ringIdx) => {
    const radius = RING_BASE + ringIdx * RING_STEP;
    const span = 360 / ring.areas.length;
    // keep the round caps of neighbouring arcs from touching
    const inset = ((STROKE / 2) / radius) * (180 / Math.PI) + EXTRA_GAP_DEG;
    ring.areas.forEach((key, i) => {
      const a0 = -90 + i * span + inset;
      const a1 = -90 + (i + 1) * span - inset;
      out.push({ key, ringLabel: ring.label, radius, a0, a1, mid: (a0 + a1) / 2, index: index++ });
    });
  });
  return out;
};

const easeOutSpring = (t: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2; // overshoots like cubic-bezier(.34,1.56,.64,1)
};

// Tweens toward `target` (arc morph). First run = load sweep with a stagger.
function useTween(target: number, initialDelay: number, skip: boolean) {
  const [value, setValue] = useState(skip ? target : 0);
  const from = useRef(skip ? target : 0);
  const first = useRef(true);
  useEffect(() => {
    if (skip) {
      from.current = target;
      setValue(target);
      return;
    }
    const isFirst = first.current;
    first.current = false;
    const start = performance.now() + (isFirst ? initialDelay : 0);
    const duration = isFirst ? LOAD_MS : T.ms.xslow;
    const origin = from.current;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - start) / duration));
      const v = origin + (target - origin) * easeOutSpring(t);
      from.current = v;
      setValue(v);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, initialDelay, skip]);
  return value;
}

// Spec transitions.score_count: count up over `slow`.
function useCountUp(target: number | null, skip: boolean) {
  const [value, setValue] = useState<number | null>(skip ? target : 0);
  const from = useRef<number>(skip || target == null ? target ?? 0 : 0);
  useEffect(() => {
    if (target == null) {
      setValue(null);
      return;
    }
    if (skip) {
      from.current = target;
      setValue(target);
      return;
    }
    const origin = from.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / T.ms.slow);
      const v = origin + (target - origin) * (1 - (1 - t) ** 3);
      from.current = v;
      setValue(Math.round(v));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, skip]);
  return value;
}

type Effect = "up" | "down" | "burst";
type Tip = { key: DimensionKey; x: number; y: number };

const Segment: React.FC<{
  seg: Seg;
  dim: RingDimension;
  selected: boolean;
  dimmed: boolean;
  hovered: boolean;
  focused: boolean;
  effect: Effect | null;
  reduced: boolean;
  onSelect: () => void;
  onHover: (e: React.PointerEvent | null) => void;
}> = ({ seg, dim, selected, dimmed, hovered, focused, effect, reduced, onSelect, onHover }) => {
  const L = useLifeAreas();
  const { t } = useTranslation();
  const area = L.area(dim.key);
  const active = dim.isActive;
  const target = active && dim.score != null ? dim.score / L.config.scale.storageMax : 0;
  const frac = Math.max(0, Math.min(1, useTween(target, seg.index * STAGGER_MS, reduced)));
  const color = active ? area.color : T.color.bg.overlay;
  const [ex, ey] = pt(seg.radius, seg.mid);
  const score = L.display(dim.score);
  const band = L.bandFor(score);
  const label = !active
    ? t("lifeAreas.chart.segOff", { area: area.label })
    : score == null
      ? t("lifeAreas.chart.segUnrated", { area: area.label })
      : t("lifeAreas.chart.segScore", { area: area.label, score, max: L.config.scale.max, band: band?.label ?? "" });

  const animClass = reduced || !effect ? "" : effect === "down" ? "lg2-dim" : "lg2-pulse";

  return (
    <g
      role="button"
      aria-label={label}
      aria-pressed={selected}
      opacity={dimmed ? 0.4 : 1}
      style={{
        cursor: active ? "pointer" : "default",
        transformOrigin: `${C}px ${C}px`,
        transform: selected && !reduced ? `scale(${SELECT_SCALE})` : "scale(1)",
        transition: reduced ? "none" : `opacity ${T.ms.fast}ms, transform ${T.ms.normal}ms ${T.ease.default}`,
      }}
      onPointerEnter={(e) => active && e.pointerType === "mouse" && onHover(e)}
      onPointerMove={(e) => active && e.pointerType === "mouse" && onHover(e)}
      onPointerLeave={(e) => e.pointerType === "mouse" && onHover(null)}
      onClick={() => active && onSelect()}
    >
      <g
        className={animClass}
        style={{
          transformOrigin: `${C}px ${C}px`,
          filter: hovered && active ? `drop-shadow(0 0 10px ${area.color})` : undefined,
          transition: reduced ? "none" : `filter ${T.ms.fast}ms`,
        }}
      >
        {focused && (
          <path
            d={arc(seg.radius, seg.a0, seg.a1)}
            fill="none"
            stroke={T.color.primary}
            strokeOpacity={0.55}
            strokeWidth={STROKE + 10}
            strokeLinecap="round"
          />
        )}
        <path
          d={arc(seg.radius, seg.a0, seg.a1)}
          fill="none"
          stroke={color}
          strokeOpacity={active ? (hovered ? 0.31 : 0.16) : 0.6}
          strokeWidth={STROKE}
          strokeLinecap="round"
        />
        {frac > 0.001 && (
          <path
            d={arc(seg.radius, seg.a0, seg.a0 + (seg.a1 - seg.a0) * frac)}
            fill="none"
            stroke={color}
            strokeOpacity={hovered ? 1 : 0.85}
            strokeWidth={STROKE}
            strokeLinecap="round"
          />
        )}
      </g>
      <text
        x={ex}
        y={ey}
        fontSize="20"
        textAnchor="middle"
        dominantBaseline="central"
        opacity={active ? 1 : 0.35}
        pointerEvents="none"
      >
        {area.emoji}
      </text>

      {effect === "burst" && !reduced && (
        <g pointerEvents="none">
          {Array.from({ length: 10 }, (_, i) => {
            const ang = seg.mid + (i - 4.5) * 6;
            const [sx, sy] = pt(seg.radius + STROKE / 2 - 4, seg.mid);
            const [tx, ty] = pt(seg.radius + STROKE / 2 + 34, ang);
            return (
              <circle
                key={i}
                cx={sx}
                cy={sy}
                r={3.5}
                fill={area.color}
                className="lg2-burst"
                style={{ "--dx": `${tx - sx}px`, "--dy": `${ty - sy}px` } as React.CSSProperties}
              />
            );
          })}
        </g>
      )}
    </g>
  );
};

type Props = {
  snapshot: RingSnapshot;
  composite: Composite;
  selectedKey: DimensionKey | null;
  onSelect: (key: DimensionKey | null) => void;
  size?: number;
};

const ConcentricRingsChart: React.FC<Props> = ({ snapshot, composite, selectedKey, onSelect, size = 440 }) => {
  const reduced = useReducedMotion();
  const L = useLifeAreas();
  const { t } = useTranslation();
  const segments = useMemo(() => buildSegments(L.config.rings), [L.config.rings]);
  // room for the outermost ring (and its glow), whatever the config has
  const half = Math.max(C, RING_BASE + (L.config.rings.length - 1) * RING_STEP + STROKE / 2 + 24);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const [effects, setEffects] = useState<Partial<Record<DimensionKey, Effect>>>({});
  const prevScores = useRef<Partial<Record<DimensionKey, number | null>> | null>(null);
  const shownScore = useCountUp(composite.score, reduced);

  const dimOf = (k: DimensionKey) => snapshot.dimensions.find((d) => d.key === k);

  // After an update: pulse a rise, dim a fall, burst on a perfect score.
  useEffect(() => {
    const next: Partial<Record<DimensionKey, number | null>> = {};
    for (const d of snapshot.dimensions) next[d.key] = d.score;
    const prev = prevScores.current;
    prevScores.current = next;
    if (!prev) return; // first render: just the load sweep
    const fx: Partial<Record<DimensionKey, Effect>> = {};
    for (const d of snapshot.dimensions) {
      const before = prev[d.key];
      if (before == null || d.score == null || before === d.score) continue;
      fx[d.key] = d.score === L.config.scale.storageMax ? "burst" : d.score > before ? "up" : "down";
    }
    if (!Object.keys(fx).length) return;
    setEffects(fx);
    const id = setTimeout(() => setEffects({}), EFFECT_MS);
    return () => clearTimeout(id);
  }, [snapshot, L.config.scale.storageMax]);

  const onHover = (key: DimensionKey) => (e: React.PointerEvent | null) => {
    if (!e) return setTip(null);
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setTip({ key, x: e.clientX - r.left, y: e.clientY - r.top });
  };

  const focusedSeg = focusIdx != null ? segments[focusIdx] : null;
  const activeKey = tip?.key ?? selectedKey ?? focusedSeg?.key ?? null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    const n = segments.length;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIdx((i) => (i == null ? 0 : (i + 1) % n));
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIdx((i) => (i == null ? n - 1 : (i - 1 + n) % n));
    } else if ((e.key === "Enter" || e.key === " ") && focusedSeg && dimOf(focusedSeg.key)?.isActive) {
      e.preventDefault();
      onSelect(selectedKey === focusedSeg.key ? null : focusedSeg.key);
    } else if (e.key === "Escape") {
      onSelect(null);
    }
  };

  const tipDim = tip ? dimOf(tip.key) : null;
  const tipScore = tipDim ? L.display(tipDim.score) : null;
  const tipBand = L.bandFor(tipScore);

  return (
    <div
      ref={wrapRef}
      className="relative mx-auto outline-none"
      style={{ width: size, maxWidth: "100%", aspectRatio: "1 / 1" }}
      tabIndex={0}
      role="group"
      aria-label={t("lifeAreas.chart.label", {
        title: L.config.center.title,
        state: L.config.center.states[composite.trend],
        score:
          composite.score == null
            ? ""
            : t("lifeAreas.chart.scoreSuffix", { score: composite.score, max: L.config.scale.max }),
        count: L.areas.length,
      })}
      onKeyDown={onKeyDown}
      onBlur={() => setFocusIdx(null)}
    >
      <style>{`
        @keyframes lg2-core-pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.04)} }
        @keyframes lg2-pulse { 0%{transform:scale(1)} 50%{transform:scale(1.05)} 100%{transform:scale(1)} }
        @keyframes lg2-dim { 0%{opacity:1} 50%{opacity:.55} 100%{opacity:1} }
        @keyframes lg2-burst { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(var(--dx),var(--dy));opacity:0} }
        .lg2-core{animation:lg2-core-pulse 3s ${T.ease.default} infinite}
        .lg2-pulse{animation:lg2-pulse ${T.ms.normal}ms ease-out 1}
        .lg2-dim{animation:lg2-dim ${EFFECT_MS}ms ease-in-out 1}
        .lg2-burst{animation:lg2-burst ${EFFECT_MS}ms ease-out forwards}
        @media (prefers-reduced-motion: reduce){ .lg2-core,.lg2-pulse,.lg2-dim,.lg2-burst{animation:none} }
      `}</style>

      <svg
        viewBox={`${C - half} ${C - half} ${2 * half} ${2 * half}`}
        width="100%"
        height="100%"
        style={{ overflow: "visible" }}
      >
        <defs>
          <radialGradient id="lg2-core-fill" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor={T.color.primaryButton} />
            <stop offset="100%" stopColor={T.color.secondary} />
          </radialGradient>
        </defs>

        {/* ring bases, visible in the gaps between area arcs */}
        {L.config.rings.map((r, i) => (
          <circle
            key={r.id}
            cx={C}
            cy={C}
            r={RING_BASE + i * RING_STEP}
            fill="none"
            stroke={T.color.bg.overlay}
            strokeOpacity={0.8}
            strokeWidth={STROKE}
          />
        ))}

        {segments.map((seg, i) => {
          const dim = dimOf(seg.key);
          if (!dim) return null;
          return (
            <Segment
              key={seg.key}
              seg={seg}
              dim={dim}
              selected={selectedKey === seg.key}
              dimmed={activeKey != null && activeKey !== seg.key}
              hovered={tip?.key === seg.key}
              focused={focusIdx === i}
              effect={effects[seg.key] ?? null}
              reduced={reduced}
              onSelect={() => onSelect(selectedKey === seg.key ? null : seg.key)}
              onHover={onHover(seg.key)}
            />
          );
        })}

        {/* Core Self */}
        <g
          className="lg2-core"
          style={{
            transformOrigin: `${C}px ${C}px`,
            filter: "drop-shadow(0 0 12px rgba(108,99,255,0.28))",
          }}
        >
          <circle cx={C} cy={C} r={CORE_R} fill="url(#lg2-core-fill)" />
        </g>
        <g pointerEvents="none" textAnchor="middle" style={{ fontFamily: T.font.family }}>
          <text x={C} y={C - 8} fontSize="15.5" fontWeight="700" fill="#fff">
            {L.config.center.title}
          </text>
          <text x={C} y={C + 12} fontSize="12" fill="rgba(255,255,255,0.9)">
            {L.config.center.states[composite.trend]}
          </text>
          {L.config.center.showScore && shownScore != null && (
            <text x={C} y={C + 33} fontSize="11.5" fill="rgba(255,255,255,0.7)">
              {shownScore}
            </text>
          )}
        </g>
      </svg>

      {tip && tipDim && (
        <div
          role="tooltip"
          className="pointer-events-none absolute hidden sm:block"
          style={{
            left: tip.x + 14,
            top: tip.y + 14,
            zIndex: 60,
            background: T.color.bg.modal,
            border: `1px solid ${withAlpha(L.area(tip.key).color, 0.5)}`,
            borderRadius: T.radius.lg,
            boxShadow: T.shadow.md,
            padding: "8px 12px",
            color: T.color.text.primary,
            whiteSpace: "nowrap",
            ...fs("sm"),
          }}
        >
          <div style={{ fontWeight: 600 }}>
            {L.area(tip.key).emoji} {L.area(tip.key).label}
          </div>
          <div style={{ ...fs("xs"), color: tipBand ? ink(tipBand.color) : T.color.text.secondary }}>
            {tipScore == null ? t("lifeAreas.detail.notRated") : `${tipBand?.label} · ${tipScore}`}
          </div>
          <div style={{ ...fs("xs"), color: T.color.text.secondary }}>
            {segments.find((s) => s.key === tip.key)?.ringLabel}
          </div>
        </div>
      )}

      <span className="sr-only" aria-live="polite">
        {focusedSeg
          ? `${L.area(focusedSeg.key).label}, ${
              L.display(dimOf(focusedSeg.key)?.score ?? null) ?? t("lifeAreas.detail.notRated")
            }`
          : ""}
      </span>
    </div>
  );
};

export default ConcentricRingsChart;
